import React, { ReactNode } from 'react';
import FuturisticButton from './FuturisticButton';

interface FuturisticHeroProps {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  illustration?: ReactNode;
  animatedBg?: boolean;
  variant?: 'primary' | 'secondary' | 'accent' | 'dark';
  className?: string;
}

const FuturisticHero: React.FC<FuturisticHeroProps> = ({
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  illustration,
  animatedBg = false,
  variant = 'primary',
  className = '',
}) => {
  return (
    <div className={`relative overflow-hidden bg-neu-bg pt-28 pb-14 ${className}`}>
      {/* Decorative neumorphic concentric circles — ambient art (right-side accent) */}
      <div className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden" style={{ width: '38%' }}>
        <div className="relative flex items-center justify-center" style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}>
          {/* Outer extruded ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: 380,
              height: 380,
              boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)',
              background: '#E0E5EC',
            }}
          />
          {/* Middle inset ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: 260,
              height: 260,
              boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)',
              background: '#E0E5EC',
            }}
          />
          {/* Inner extruded circle */}
          <div
            className="absolute rounded-full"
            style={{
              width: 140,
              height: 140,
              boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)',
              background: '#E0E5EC',
            }}
          />
          {/* Accent core */}
          <div
            className="absolute rounded-full animate-float"
            style={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)',
              boxShadow: '0 4px 16px rgba(108,99,255,0.45)',
            }}
          />
        </div>
      </div>

      {/* Hero content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Text content */}
          <div className="flex-1 text-center lg:text-left">
            {typeof title === 'string' ? (
              <h1 className="font-display text-5xl md:text-6xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
                {title}
              </h1>
            ) : (
              title
            )}

            {subtitle && (
              typeof subtitle === 'string' ? (
                <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  {subtitle}
                </p>
              ) : (
                subtitle
              )
            )}

            {(primaryButtonText || secondaryButtonText) && (
              <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center lg:justify-start">
                {primaryButtonText && (
                  <FuturisticButton
                    variant="accent"
                    size="lg"
                    onClick={onPrimaryClick}
                  >
                    {primaryButtonText}
                  </FuturisticButton>
                )}
                {secondaryButtonText && (
                  <FuturisticButton
                    variant="outline"
                    size="lg"
                    onClick={onSecondaryClick}
                  >
                    {secondaryButtonText}
                  </FuturisticButton>
                )}
              </div>
            )}
          </div>

          {/* Illustration — hidden on mobile to keep hero compact */}
          {illustration && (
            <div className="hidden lg:flex flex-1 justify-end">
              <div className="relative max-w-md w-full transition-transform duration-500 hover:scale-105">
                {illustration}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuturisticHero;
